/**
 * FAQ tabs frontend JavaScript.
 * Handles tab switching for the dedicated FAQ tabs block.
 *
 * @class
 */
class FaqTabs {

	static counter = 0;

	block = null;

	panels = null;

	buttonsContainer = null;

	buttonItems = null;

	buttons = null;

	clickHandlers = new Map();

	/**
	 * @param {HTMLElement} block FAQ tabs block element.
	 */
	constructor( block ) {

		this.block = block;
		this.panels = block.querySelectorAll( '.b-faq-tabs__panels > .b-faq-tab' );

		if ( !this.panels.length ) {

			return;

		}

		this.buttonsContainer = block.querySelector( '.b-faq-tabs__buttons' );
		this.init();

	}

	/**
	 * Initialize FAQ tabs.
	 *
	 * @return {void}
	 */
	init = () => {

		this.generateButtons();
		this.buttonItems = this.block.querySelectorAll(
			'.b-faq-tabs__buttons .b-faq-tab__button-item',
		);
		this.buttons = this.block.querySelectorAll(
			'.b-faq-tabs__buttons .b-faq-tab__button',
		);
		this.initializeTabs();
		this.attachEventListeners();

	};

	/**
	 * Generate tab buttons from tab panels.
	 *
	 * @return {void}
	 */
	generateButtons = () => {

		if ( !this.buttonsContainer || !this.panels.length ) {

			return;

		}

		const tabsClasses = this.block.className
			.split( ' ' )
			.filter( ( cls ) => cls.startsWith( 'is-style-' ) );

		if ( tabsClasses.length > 0 ) {

			tabsClasses.forEach( ( cls ) => {

				this.buttonsContainer.classList.add( cls );

			} );

		}

		this.buttonsContainer.innerHTML = '';

		this.panels.forEach( ( panel, index ) => {

			const buttonItem = document.createElement( 'div' );
			const button = document.createElement( 'button' );
			const label = document.createElement( 'span' );
			FaqTabs.counter += 1;
			const uniqueId = `faq-tab-${FaqTabs.counter}-${index}`;
			const panelId = `faq-tab-panel-${uniqueId}`;
			const buttonId = `faq-tab-button-${uniqueId}`;
			const tabTitle =
				panel.getAttribute( 'data-tab-title' ) || `Tab ${index + 1}`;
			const tabImageUrl =
				panel.getAttribute( 'data-tab-image-url' ) || '';
			const tabImageAlt =
				panel.getAttribute( 'data-tab-image-alt' ) || '';
			const tabImageWidth = Number.parseInt(
				panel.getAttribute( 'data-tab-image-width' ) || '0',
				10,
			);
			const tabImageHeight = Number.parseInt(
				panel.getAttribute( 'data-tab-image-height' ) || '0',
				10,
			);

			panel.id = panelId;
			panel.setAttribute( 'aria-labelledby', buttonId );

			button.id = buttonId;
			button.setAttribute( 'role', 'tab' );
			button.setAttribute( 'aria-controls', panelId );
			button.setAttribute( 'aria-selected', index === 0 ? 'true' : 'false' );
			button.setAttribute( 'tabindex', index === 0 ? '0' : '-1' );
			button.setAttribute( 'type', 'button' );
			buttonItem.className = `b-faq-tab__button-item ${index === 0 ? 'active' : ''}`.trim();
			button.className = `b-faq-tab__button ${index === 0 ? 'active' : ''}`.trim();

			if ( tabImageUrl ) {

				const image = document.createElement( 'img' );
				image.className = 'b-faq-tab__button-image';
				image.src = tabImageUrl;
				image.alt = tabImageAlt;
				image.loading = 'lazy';

				if ( tabImageWidth > 0 ) {

					image.width = tabImageWidth;

				}

				if ( tabImageHeight > 0 ) {

					image.height = tabImageHeight;

				}

				buttonItem.appendChild( image );
				buttonItem.classList.add( 'has-image' );

			}

			label.className = 'b-faq-tab__button-label';
			label.textContent = tabTitle;
			button.appendChild( label );
			buttonItem.appendChild( button );
			this.buttonsContainer.appendChild( buttonItem );

		} );

	};

	/**
	 * Activate initial tab.
	 *
	 * @return {void}
	 */
	initializeTabs = () => {

		this.deactivateAll();
		this.activate( 0 );

	};

	/**
	 * Attach button and keyboard listeners.
	 *
	 * @return {void}
	 */
	attachEventListeners = () => {

		this.buttonItems.forEach( ( buttonItem, index ) => {

			/**
			 * Click handler for FAQ tab item.
			 *
			 * @return {void}
			 */
			const handler = () => {

				this.deactivateAll();
				this.activate( index );

			};

			this.clickHandlers.set( buttonItem, handler );
			buttonItem.addEventListener( 'click', handler );

		} );

		if ( this.buttonsContainer ) {

			this.buttonsContainer.addEventListener( 'keydown', this.handleKeyDown );

		}

	};

	/**
	 * Remove event listeners.
	 *
	 * @return {void}
	 */
	destroy = () => {

		this.clickHandlers.forEach( ( handler, item ) => {

			item.removeEventListener( 'click', handler );

		} );

		this.clickHandlers.clear();

		if ( this.buttonsContainer ) {

			this.buttonsContainer.removeEventListener( 'keydown', this.handleKeyDown );

		}

	};

	/**
	 * Deactivate all tabs and panels.
	 *
	 * @return {void}
	 */
	deactivateAll = () => {

		this.buttonItems.forEach( ( buttonItem ) => {

			buttonItem.classList.remove( 'active' );

		} );

		this.buttons.forEach( ( button ) => {

			button.classList.remove( 'active' );
			button.setAttribute( 'aria-selected', 'false' );
			button.setAttribute( 'tabindex', '-1' );

		} );

		this.panels.forEach( ( panel ) => {

			panel.classList.remove( 'active' );
			panel.hidden = true;

		} );

	};

	/**
	 * Activate tab and panel by index.
	 *
	 * @param {number} index Tab index.
	 * @return {void}
	 */
	activate = ( index ) => {

		if ( index < 0 || index >= this.buttons.length ) {

			return;

		}

		if ( this.buttons[index] ) {

			this.buttonItems[index]?.classList.add( 'active' );
			this.buttons[index].classList.add( 'active' );
			this.buttons[index].setAttribute( 'aria-selected', 'true' );
			this.buttons[index].setAttribute( 'tabindex', '0' );

		}

		if ( this.panels[index] ) {

			this.panels[index].classList.add( 'active' );
			this.panels[index].hidden = false;

		}

	};

	/**
	 * Move focus to target tab.
	 *
	 * @param {number} index Tab index.
	 * @return {void}
	 */
	focusTab = ( index ) => {

		if ( index < 0 || index >= this.buttons.length ) {

			return;

		}

		this.buttons.forEach( ( button, buttonIndex ) => {

			button.setAttribute( 'tabindex', buttonIndex === index ? '0' : '-1' );

		} );

		this.buttons[index]?.focus();

	};

	/**
	 * Handle keyboard navigation.
	 *
	 * @param {KeyboardEvent} event Keyboard event.
	 * @return {void}
	 */
	handleKeyDown = ( event ) => {

		const focusedEl = this.buttonsContainer.ownerDocument.activeElement;
		const currentIndex = Array.from( this.buttons ).indexOf( focusedEl );

		if ( currentIndex === -1 ) {

			return;

		}

		let shouldPrevent = false;

		if ( event.key === 'ArrowRight' ) {

			shouldPrevent = true;
			this.focusTab( ( currentIndex + 1 ) % this.buttons.length );

		}

		if ( event.key === 'ArrowLeft' ) {

			shouldPrevent = true;
			this.focusTab(
				( currentIndex - 1 + this.buttons.length ) % this.buttons.length,
			);

		}

		if ( event.key === 'Home' ) {

			shouldPrevent = true;
			this.focusTab( 0 );

		}

		if ( event.key === 'End' ) {

			shouldPrevent = true;
			this.focusTab( this.buttons.length - 1 );

		}

		if ( event.key === 'Enter' || event.key === ' ' ) {

			shouldPrevent = true;
			this.deactivateAll();
			this.activate( currentIndex );

		}

		if ( shouldPrevent ) {

			event.preventDefault();

		}

	};

}

document.querySelectorAll( '.b-faq-tabs' ).forEach( ( block ) => {

	new FaqTabs( block );

} );
